// screen: Connect an app (WordPress plugin, 2026-10-07) — https://studio.viewstream.co.il/connect?app=wordpress&site=…
// &redirect_uri=…&state=…&code_challenge=…&code_challenge_method=S256. The signed-in user picks the tenant, sees the
// site and the exact scopes, and approves: the control plane stores a 60-second one-time code (POST
// /v1/connect/authorize) and the browser returns to the site, whose server exchanges the code for a scoped API key
// named "WordPress: <host>" (revocable in Integrations). The control plane validates redirect_uri (the site's own
// origin, https) and PKCE; this page never sends the browser anywhere the API did not approve.
import * as React from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ApiError, type ConnectAuthorizeInput, type ConnectCheck } from '@viewstream/api-client';
import { Badge, Button, Select, Skeleton } from '@viewstream/ui';
import { api } from '~/app/api';
import { useAuth } from '~/app/auth';
import { useLocale } from '~/app/locale';
import { ProductName, LocaleToggle } from '~/app/brand';

type Params = Omit<ConnectAuthorizeInput, 'check'>;
const STASH = 'vs-connect-request';
const str = (v: unknown) => (typeof v === 'string' ? v : '');

export const Route = createFileRoute('/connect')({
  validateSearch: (s: Record<string, unknown>) => ({
    app: str(s['app']), site: str(s['site']), redirect_uri: str(s['redirect_uri']), state: str(s['state']),
    code_challenge: str(s['code_challenge']), code_challenge_method: str(s['code_challenge_method']) || 'S256',
  }),
  component: Connect,
});

/** The request from the URL, or (after the login round trip, which keeps only the path) from sessionStorage. */
function useRequest(): Params | null {
  const s = Route.useSearch();
  return React.useMemo(() => {
    if (s.app && s.site && s.redirect_uri) {
      // exactly the API's fields (the router keeps other query parameters such as lang)
      const p: Params = {
        app: s.app as Params['app'], site: s.site, redirect_uri: s.redirect_uri, state: s.state,
        code_challenge: s.code_challenge, code_challenge_method: s.code_challenge_method as 'S256',
      };
      try { sessionStorage.setItem(STASH, JSON.stringify(p)); } catch { /* private mode: the URL still has it */ }
      return p;
    }
    try {
      const v = JSON.parse(sessionStorage.getItem(STASH) ?? 'null') as Params | null;
      return v && v.app && v.site && v.redirect_uri ? v : null;
    } catch { return null; }
  }, [s]);
}

function problemText(e: unknown): string {
  if (e instanceof ApiError) return e.problem?.errors?.map((x) => x.detail).join(' · ') || e.problem?.detail || e.message;
  return e instanceof Error ? e.message : String(e);
}

function Connect() {
  const { t } = useLingui();
  const { me, loading, switchTenant } = useAuth();
  useLocale();
  const req = useRequest();
  const [check, setCheck] = React.useState<ConnectCheck | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [tenant, setTenant] = React.useState<string>('');
  React.useEffect(() => { document.title = `${t`Connect an app`} · ViewStream Studio`; }, [t]);

  const runCheck = React.useCallback(async () => {
    if (!req) return;
    setErr(null); setCheck(null);
    try {
      const r = await api.POST('/v1/connect/authorize', { body: { ...req, check: true } });
      setCheck(r.data ?? null);
    } catch (e) { setErr(problemText(e)); }
  }, [req]);

  React.useEffect(() => {
    if (me?.user && req) {
      setTenant(me.current_tenant?.slug ?? '');
      void runCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per sign-in; tenant changes re-check explicitly
  }, [me?.user?.id, req]);

  const scopeLabels: Record<string, string> = {
    'assets:read': t`Read your videos`,
    'assets:write': t`Upload videos and edit their details`,
    'clips:read': t`Read your clips`,
    'channels:read': t`Read channels and the broadcast schedule (catch-up)`,
    'ai:read': t`Read AI articles, summaries and presenters`,
    'webhooks:manage': t`Register a webhook so new videos reach the site`,
  };

  if (__APP__ === 'admin') return <Navigate to="/login" search={{ next: undefined }} />;
  if (loading) return <div className="p-8"><Skeleton className="h-8 w-48" /></div>;
  if (!me?.user) return <Navigate to="/login" search={{ next: '/connect' }} />;

  async function pickTenant(slug: string) {
    setTenant(slug); setBusy(true);
    try { await switchTenant(slug); await runCheck(); } catch (e) { setErr(problemText(e)); } finally { setBusy(false); }
  }

  async function approve() {
    if (!req) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.POST('/v1/connect/authorize', { body: req });
      try { sessionStorage.removeItem(STASH); } catch { /* ignore */ }
      if (r.data?.redirect) window.location.assign(r.data.redirect);
    } catch (e) { setErr(problemText(e)); setBusy(false); }
  }

  function cancel() {
    try { sessionStorage.removeItem(STASH); } catch { /* ignore */ }
    if (check && req) { // only to a redirect_uri the API accepted for this site
      const u = new URL(req.redirect_uri);
      u.searchParams.set('error', 'access_denied');
      u.searchParams.set('state', req.state);
      window.location.assign(u.toString());
    } else window.location.assign('/');
  }

  const missing = check?.missing_scopes ?? [];
  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <div className="w-full max-w-lg rounded-xl border border-border bg-surface p-8 shadow-card">
        <div className="mb-6 flex items-center justify-between">
          <ProductName large />
          <LocaleToggle />
        </div>
        <h1 className="mb-2 text-xl font-semibold">{t`Connect an app to ViewStream`}</h1>
        {!req ? (
          <p className="text-sm text-danger" role="alert">{t`This connection link is incomplete. Start again from the app (for WordPress: Settings → ViewStream → Connect to ViewStream).`}</p>
        ) : (
          <>
            <p className="mb-4 text-sm text-fg-muted">
              {t`A site asks to connect to your ViewStream account. Approve only if you started this from your own site.`}
            </p>
            <dl className="mb-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-fg-muted">{t`App`}</dt>
              <dd>{check?.app_name ?? req.app}</dd>
              <dt className="text-fg-muted">{t`Site`}</dt>
              <dd className="ltr-isolate break-all font-mono" dir="ltr">{check?.origin ?? req.site}</dd>
              {check && <><dt className="text-fg-muted">{t`API key name`}</dt><dd className="ltr-isolate" dir="ltr">{check.key_name}</dd></>}
            </dl>
            <label className="mb-1 block text-sm font-medium" htmlFor="connect-tenant">{t`Tenant`}</label>
            <Select ariaLabel={t`Tenant`} value={tenant} onValueChange={(v) => void pickTenant(v)} className="mb-4 w-full"
              options={me.tenants.map((x) => ({ value: x.slug, label: `${x.name} (${x.slug})` }))} />
            {check && (
              <>
                <h2 className="mb-2 text-sm font-semibold">{t`The app will be able to:`}</h2>
                <ul className="mb-4 space-y-1 text-sm">
                  {check.scopes.map((s) => (
                    <li key={s} className="flex items-center gap-2">
                      <Badge tone={missing.includes(s) ? 'danger' : 'neutral'} className="ltr-isolate font-mono" dir="ltr">{s}</Badge>
                      <span>{scopeLabels[s] ?? s}</span>
                    </li>
                  ))}
                </ul>
                <p className="mb-4 text-xs text-fg-muted">
                  {t`Approving creates an API key with exactly these permissions. You can revoke it at any time in Integrations → API keys, or with "Disconnect" in the app.`}
                </p>
                {missing.length > 0 && (
                  <p className="mb-4 text-sm text-danger" role="alert">{t`Your role in this tenant cannot grant every permission the app needs. Ask a tenant admin to connect it, or pick another tenant.`}</p>
                )}
              </>
            )}
            {err && <p className="mb-4 text-sm text-danger" role="alert">{err}</p>}
            <div className="flex gap-2">
              <Button onClick={() => void approve()} loading={busy} disabled={!check || missing.length > 0}>{t`Approve and connect`}</Button>
              <Button variant="secondary" onClick={cancel} disabled={busy}>{t`Cancel`}</Button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
